import { meKeys } from '../lib/meQueryKeys.ts';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Field, TextInput } from '@l2u/ui/components/Field.tsx';
import { useAbnVerification } from '@l2u/ui/lib/useAbnVerification.ts';
import { useAutoFillField } from '@l2u/ui/lib/autoFillField.ts';
import { AbnStatusIndicator } from '@l2u/ui/components/AbnStatus.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { companyInfoLabel } from '@l2u/ui/lib/companyInfo.ts';
import type { VendorCompany } from '@l2u/ui/lib/types.ts';
import { REQUIRED_COMPANY_INFO_FIELDS } from '@l2u/shared/progress';

type SelfForm = {
  abn: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  legal_entity_name: string;
  address_line: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
};

function toSelfForm(c: VendorCompany): SelfForm {
  return {
    abn: c.abn ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    legal_entity_name: c.legal_entity_name ?? '',
    address_line: c.address_line ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postcode: c.postcode ?? '',
    country: c.country ?? '',
  };
}

export function CompanyInformation() {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();

  const { data, isLoading } = useQuery({
    queryKey: meKeys.company(),
    queryFn: () => companyApi.get<{ company: VendorCompany }>('/api/me'),
  });

  // Seeded in the initializer, not only by the effect below: with a warm ['me']
  // cache `data` is already here on the first render, so deriving the form now
  // skips a spinner frame (and lets the page render without committing effects).
  // A cold load still starts null — the fetch is pending — and the effect seeds
  // it on arrival, and re-seeds it whenever the admin's edits change updated_at.
  const [form, setForm] = useState<SelfForm | null>(() =>
    data?.company ? toSelfForm(data.company) : null,
  );
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data?.company) setForm(toSelfForm(data.company));
  }, [data?.company.id, data?.company.updated_at]);

  const company = data?.company;
  // `review` (submitted, awaiting admin) locks the whole form. `compliant`
  // (approved) keeps the form editable but freezes the two identity fields —
  // ABN and legal entity name — which only an admin can change afterwards.
  const fullyLocked = company?.status === 'review';
  const approved = company?.status === 'compliant';
  // ABN + legal entity name lock from the first submission onward — including
  // after a resend reverts a once-compliant vendor back to in_progress.
  const identityLocked = fullyLocked || approved || !!company?.has_submitted_before;

  const missing = useMemo(() => {
    if (!company) return [] as string[];
    const result: string[] = [];
    if (!company.abn) result.push('abn');
    if (!company.primary_contact_name) result.push('primary_contact_name');
    if (!company.contact_role) result.push('contact_role');
    if (!company.contact_phone) result.push('contact_phone');
    if (!company.contact_email) result.push('contact_email');
    if (company.employee_count === null) result.push('employee_count');
    if (!company.legal_entity_name) result.push('legal_entity_name');
    if (!company.address_line) result.push('address_line');
    if (!company.city) result.push('city');
    if (!company.state) result.push('state');
    if (!company.postcode) result.push('postcode');
    if (!company.country) result.push('country');
    return result;
  }, [company]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!form) throw new Error('Form not ready');
      const payload: Record<string, unknown> = {
        abn: form.abn.trim() || null,
        trustee_info: form.trustee_info.trim() || null,
        primary_contact_name: form.primary_contact_name.trim() || null,
        contact_role: form.contact_role.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
        contact_email: form.contact_email.trim() || null,
        legal_entity_name: form.legal_entity_name.trim() || null,
        address_line: form.address_line.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        postcode: form.postcode.trim() || null,
        country: form.country.trim() || null,
      };
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return companyApi.patch<{ company: VendorCompany }>('/api/me', payload);
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      void qc.invalidateQueries({ queryKey: meKeys.all });
    },
    onError: (err) => setError(err),
  });

  // Live ABN verification — skipped whenever the ABN field is read-only
  // (under review or post-approval) by passing ''.
  const abnCheck = useAbnVerification(identityLocked ? '' : (form?.abn ?? ''), 'company');

  // Auto-fill registered details from a verified ABN. Smart-sync: fills when
  // blank or still holding our own prior fill; never clobbers a manual edit.
  // Must run above the early return below to satisfy the Rules of Hooks.
  const abnFill = abnCheck.state === 'verified' ? abnCheck.result : null;
  useAutoFillField(abnFill?.entityName ?? null, form?.legal_entity_name ?? '', (v) =>
    set('legal_entity_name', v),
  );
  useAutoFillField(abnFill?.addressState ?? null, form?.state ?? '', (v) => set('state', v));
  useAutoFillField(abnFill?.addressPostcode ?? null, form?.postcode ?? '', (v) =>
    set('postcode', v),
  );

  if (isLoading || !form || !company) return <CenteredSpinner />;

  function set<K extends keyof SelfForm>(key: K, value: SelfForm[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Vendor information</h1>
        <p className="mt-1 text-sm text-slate-500">
          Fill in your business details. Required fields must be completed before you can submit the
          questionnaire.
        </p>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            About your business
          </h2>
          <div className="flex items-center gap-2">
            {savedAt && <span className="text-xs text-emerald-600">Saved</span>}
            <Btn
              size="sm"
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={fullyLocked}
            >
              Save
            </Btn>
          </div>
        </div>

        {fullyLocked && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Submitted — fields are locked for review.
          </div>
        )}

        {identityLocked && !fullyLocked && (
          <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            ABN and legal entity name are locked after submission. You can still update your other
            details below — contact us if your ABN or legal entity needs to change.
          </div>
        )}

        {missing.length > 0 && !fullyLocked && !approved && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {missing.length} required field{missing.length === 1 ? '' : 's'} remaining before you
            can submit the questionnaire.
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field
            label="ABN"
            required
            hint={
              identityLocked && !fullyLocked
                ? 'Locked after submission — contact us to change.'
                : undefined
            }
          >
            <TextInput
              value={form.abn}
              disabled={identityLocked}
              onChange={(e) => set('abn', e.target.value)}
            />
            <AbnStatusIndicator state={abnCheck.state} result={abnCheck.result} />
          </Field>
          <Field
            label="Trustee Name and ACN (if applicable)"
            hint="Only required if you operate under a trust."
          >
            <TextInput
              value={form.trustee_info}
              disabled={fullyLocked}
              onChange={(e) => set('trustee_info', e.target.value)}
            />
          </Field>
          <Field
            label="Legal entity name"
            required
            hint={
              identityLocked && !fullyLocked
                ? 'Locked after submission — contact us to change.'
                : undefined
            }
          >
            <TextInput
              value={form.legal_entity_name}
              disabled={identityLocked}
              onChange={(e) => set('legal_entity_name', e.target.value)}
            />
          </Field>
          {/* companies.company_name IS the trading name — 0070 dropped the
              dedicated column. Admin-owned and deliberately absent from
              CompanyInfoSelfUpdateInput, so it reads straight off the live
              company rather than the form: whatever the admin last saved is
              what the vendor sees, and nothing here can send a value back. */}
          <Field label="Trading Name" hint="Contact us if this needs to change.">
            <TextInput value={company.company_name} disabled />
          </Field>
          <Field label="Business Address" required>
            <TextInput
              value={form.address_line}
              disabled={fullyLocked}
              onChange={(e) => set('address_line', e.target.value)}
            />
          </Field>
          <Field label="City" required>
            <TextInput
              value={form.city}
              disabled={fullyLocked}
              onChange={(e) => set('city', e.target.value)}
            />
          </Field>
          <Field label="State" required>
            <TextInput
              value={form.state}
              disabled={fullyLocked}
              onChange={(e) => set('state', e.target.value)}
            />
          </Field>
          <Field label="Postcode" required>
            <TextInput
              value={form.postcode}
              disabled={fullyLocked}
              onChange={(e) => set('postcode', e.target.value)}
            />
          </Field>
          <Field label="Country" required>
            <TextInput
              value={form.country}
              disabled={fullyLocked}
              onChange={(e) => set('country', e.target.value)}
            />
          </Field>
          <Field label={companyInfoLabel(settings, 'primaryContact')} required>
            <TextInput
              value={form.primary_contact_name}
              disabled={fullyLocked}
              onChange={(e) => set('primary_contact_name', e.target.value)}
            />
          </Field>
          <Field label="Contact role/position" required>
            <TextInput
              value={form.contact_role}
              disabled={fullyLocked}
              onChange={(e) => set('contact_role', e.target.value)}
            />
          </Field>
          <Field label="Contact phone number" required>
            <TextInput
              type="tel"
              value={form.contact_phone}
              disabled={fullyLocked}
              onChange={(e) => set('contact_phone', e.target.value)}
            />
          </Field>
          <Field label="Contact email address" required>
            <TextInput
              type="email"
              value={form.contact_email}
              disabled={fullyLocked}
              onChange={(e) => set('contact_email', e.target.value)}
            />
            {/* This field IS the login: saving it moves the vendor's auth
                identity, so a typo here costs them portal access until an
                admin fixes it. Warn before the save, not after. */}
            <p className="mt-1 text-xs text-slate-500">
              You sign in with this address. If you change it, use the new address next time you log
              in.
            </p>
          </Field>
          <Field label="# of employees" required>
            <TextInput
              type="number"
              min={0}
              value={form.employee_count}
              disabled={fullyLocked}
              onChange={(e) => set('employee_count', e.target.value)}
            />
          </Field>
        </div>
        <ErrorMessage error={error} />
      </section>
    </div>
  );
}

// Re-export so other modules can reference the same constant if needed.
export { REQUIRED_COMPANY_INFO_FIELDS };
